// The widget the agent is building, read from its tool call BEFORE the widget
// itself lands. `tool_start` already carries widget_type + title in tool_input
// (append-log.ts → SSE → ChatLogEntry), but ProcessingBubble only reads
// tool_name and throws the rest away. This recovers it so the skeleton can show
// the real shape and title instead of a generic bar shimmer.
//
// Purely derived from logs, no I/O — unit-testable without a browser.

import type { ChatLogEntry } from "@/app/api/types/chat-sessions";

// Mirrors WIDGET_TYPES in ai/agent_system/tools/domain/bi/_helpers.py.
export type WidgetShape =
  | "single_value"
  | "line_chart"
  | "bar_chart"
  | "pie_chart"
  | "table";

export interface WidgetIntent {
  shape: WidgetShape;
  title?: string;
  /** Grid column span the widget will take, when the tool call carries it —
   *  lets the board ghost match the real widget's size. Absent → the caller
   *  falls back to the same default the backend uses (see DEFAULT_WIDTH). */
  width?: number;
  /** Grid row span the widget will take, when the tool call carries it. */
  height?: number;
  /** True when the LAST widget tool of the turn was `update_widget` — an
   *  in-place edit of an existing card, NOT a new one. The chat skeleton still
   *  telegraphs the shape, but the board ghost must skip it: an update grows no
   *  widget count, so a board phantom would strand at the end of the grid for
   *  the whole grace window (STK-1917 review, greptile P1). */
  isUpdate?: boolean;
}

// The tools whose tool_input describes a widget being built. bi_query_widget is
// the merged query+widget tool; create_widget / update_widget are the classic
// path. All three put widget_type + title at the top level of tool_input.
const WIDGET_TOOLS = new Set([
  "bi_query_widget",
  "create_widget",
  "update_widget",
]);

const SHAPES = new Set<WidgetShape>([
  "single_value",
  "line_chart",
  "bar_chart",
  "pie_chart",
  "table",
]);

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

// A finite number or nothing — the tool ships width/height as JSON numbers, but
// a schema drift (string, null, NaN) must degrade to undefined so the caller
// falls back to the default span rather than emitting a broken grid value.
function asNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : undefined;
}

/**
 * The widget the current turn is building, or null when none is known yet.
 *
 * Returns the LAST widget-building tool_start in the log stream: a turn that
 * corrects a mis-typed chart (update_widget after create_widget) should show
 * the corrected shape, not the first guess. A tool_start whose widget_type is
 * absent or unrecognized yields null — the caller then falls back to the
 * generic skeleton, so a schema drift degrades gracefully rather than throwing.
 */
export function deriveWidgetIntent(
  logs: ChatLogEntry[] | undefined,
): WidgetIntent | null {
  if (!logs?.length) return null;
  let intent: WidgetIntent | null = null;
  for (const log of logs) {
    if (log.type !== "tool_start") continue;
    if (!log.tool_name || !WIDGET_TOOLS.has(log.tool_name)) continue;
    const input = log.tool_input;
    if (!input) continue;
    const rawShape = input["widget_type"];
    if (typeof rawShape !== "string" || !SHAPES.has(rawShape as WidgetShape)) {
      continue;
    }
    intent = {
      shape: rawShape as WidgetShape,
      title: asString(input["title"]),
      width: asNumber(input["width"]),
      height: asNumber(input["height"]),
      // Truthy only for an update — absent (undefined) for creates, so the
      // board-skip check reads it as falsy and existing shape/title equality
      // isn't perturbed by an always-present flag.
      isUpdate: log.tool_name === "update_widget" ? true : undefined,
    };
  }
  return intent;
}
